import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import StatusBadge from "@/components/StatusBadge";
import { Wrench, User } from "lucide-react";

// Default marker icons in webpack/CRA break unless we patch
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

export default function MapView() {
  const [data, setData] = useState({ technicians: [], complaints: [] });

  useEffect(() => {
    api.get("/map/overview").then((r) => setData(r.data));
  }, []);

  const center = [28.0, 84.0]; // Nepal centroid

  return (
    <div className="space-y-5" data-testid="map-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Map View</h1>
        <p className="text-sm text-slate-500 mt-1">Live geographic view of technicians and open complaints across Nepal.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-3">
          <Card><CardContent className="p-0">
            <div style={{ height: 600 }} data-testid="map-container">
              <MapContainer center={center} zoom={7} style={{ height: "100%", width: "100%" }}>
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {data.technicians.map((t) => (
                  <CircleMarker
                    key={`t-${t.id}`}
                    center={[t.lat, t.lng]}
                    radius={9}
                    pathOptions={{ color: t.active ? "#0f172a" : "#94a3b8", fillColor: t.active ? "#10b981" : "#cbd5e1", fillOpacity: 0.9, weight: 2 }}
                  >
                    <Popup>
                      <div className="space-y-1">
                        <div className="font-semibold text-sm flex items-center gap-1"><Wrench size={12} /> {t.name}</div>
                        <div className="text-xs text-slate-600 font-mono">{t.phone}</div>
                        <div className="text-xs">{t.province} · {t.area}</div>
                        <div className="text-xs text-slate-500">{t.category}</div>
                      </div>
                    </Popup>
                  </CircleMarker>
                ))}
                {data.complaints.map((c) => (
                  <Marker key={`c-${c.id}`} position={[c.lat, c.lng]}>
                    <Popup>
                      <div className="space-y-1 min-w-[180px]">
                        <Link to={`/complaints/${c.id}`} className="font-mono text-sm font-semibold text-blue-700 hover:underline">{c.complaint_number}</Link>
                        <div className="text-xs flex items-center gap-1"><User size={11} /> {c.customer_name}</div>
                        <div className="text-xs">{c.brand} · {c.product_name}</div>
                        <div className="text-xs text-slate-500">{c.province}, {c.area}</div>
                        <div className="text-xs">Tech: {c.technician_name || "—"}</div>
                        <div className="pt-1"><StatusBadge status={c.status} /></div>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </CardContent></Card>
        </div>

        <div className="space-y-3">
          <Card><CardContent className="p-4">
            <div className="font-heading font-semibold text-slate-900 mb-2">Legend</div>
            <div className="text-xs space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-emerald-500 border-2 border-slate-900"></div>
                <span>Active Technician</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-slate-300 border-2 border-slate-400"></div>
                <span>Inactive Technician</span>
              </div>
              <div className="flex items-center gap-2">
                <img src="https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png" alt="" className="w-4 h-6" />
                <span>Open Complaint</span>
              </div>
              <div className="text-slate-500 mt-2 leading-relaxed">
                Complaints without exact coordinates are placed at the province centroid with a small random jitter.
              </div>
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="font-heading font-semibold text-slate-900 mb-2">Summary</div>
            <div className="text-sm space-y-1">
              <div className="flex justify-between"><span>Technicians on map</span><span className="font-mono font-semibold">{data.technicians.length}</span></div>
              <div className="flex justify-between"><span>Open complaints</span><span className="font-mono font-semibold">{data.complaints.length}</span></div>
            </div>
          </CardContent></Card>
        </div>
      </div>
    </div>
  );
}
